import AlertRequestQuery from "~/entities/AlertRequestQuery";
import Box from "~/entities/Box";
import { Country } from "~/entities/Response/FormData";
import Trip from "~/entities/Trip";
import ValidationModule from "~/entities/ValidationModule";

export default class ValidationSearch extends ValidationModule {
  // alert clusters
  public countryAlerts = {
    selector: {
      from: "AlertInvalidFromCountry",
      to: "AlertInvalidCountry",
    },
    needle: ["#CountryName#"],
  };

  public rateAlerts = {
    selector: {
      default: "AlertFedExFailed",
      canada: "AlertFedExCanadaNoResults",
      timeOut: "AlertFedExTimeout",
      noResults: "AlertFedExNoResults",
    },
    needle: [""],
  };

  public nearAlerts = {
    selector: {
      dim: "AlertNearSizeLimit",
      size: "AlertNearDimLimit",
      weight: "AlertNearWeightLimit",
    },
    needle: [""],
  };

  public weightNoVerifyAlerts = {
    selector: {
      weight: "AlertHeavyWeightNoVerify",
    },
    needle: [""],
  };

  public weightVerifyAlerts = {
    selector: {
      weight: "AlertHeavyWeightVerify",
    },
    needle: [""],
  };

  public overAlerts = {
    selector: {
      dim: "AlertOverSizeLimit",
      size: "AlertOverDimLimit",
      weight: "AlertOverWeightLimit",
    },
    needle: [""],
  };

  public valueAlerts = {
    selector: {
      zero: "CommercialNoValue", // select by market value < 1?
      // low: 'PersonalNoValue',//select by market value < minMarketValue?
    },
    needle: [""],
  };

  public contentAlerts = {
    selector: {
      USeBike: "AlertEbikeShipment",
      eBike: "AlertEBikeInternational",
      scicon: "SciconWarning",
    },
    needle: [""],
  };

  public protectionAlerts = {
    selector: {
      high: "MultiplePackageHighValue", // On search step, triggers if one package's protection is greater than $1000 but another package's protection is less than $1000.
      less: "PPLessThanMV", // select by market value > premium protection?
      max: "ProtectionMax", // select by custom protection value > 20000?
    },
    needle: [""],
  };

  public purposeAlerts = {
    selector: {},
    needle: [""],
  };

  public stageAlerts = {
    selector: {
      maxStage: "AlertMaxStageWarning",
    },
    needle: [""],
  };

  public dimensionLimits = {
    in: {
      maxL: 108,
      maxW: 108,
      maxH: 108,
      maxWeight: 150,
      weightNoVerify: 70,
      weightVerify: 100,
      maxDimensional: 165,
      nearLimitDelta: 5,
    },
    cm: {
      maxL: 274,
      maxW: 274,
      maxH: 274,
      maxWeight: 68,
      weightNoVerify: 31,
      weightVerify: 45,
      maxDimensional: 419,
      nearLimitDelta: 12,
    },
  };

  public minMarketValue = 1;
  public maxMarketValue = 20000;
  public maxProtectionValue = 20000;
  public tripsLimit = 6;

  public constructor() {
    super("Search");
    this.alertNames = [
      "AlertInvalidFromCountry", // We do not currently offer shipping services from #CountryName#
      "AlertInvalidCountry", // We do not currently offer shipping services to #CountryName#.
      "AlertNearSizeLimit", // Your package dimensions or weight are close to the maximum allowed. To avoid an Overmax Penalty of US$1,025 or return of your shipment, you will be asked to verify your dimensions and weight by submitting photos documenting the measurements and weight before receiving your shipping label(s).
      "AlertOverSizeLimit", // The dimension you entered is approaching the Maximum Length of 108 in (274 cm). To avoid an Overmax Penalty of US$1,025 or return of your shipment, you will be asked to verify your dimensions and weight by submitting photos documenting the measurements and weight before receiving your shipping label(s).
      "AlertNearDimLimit", // The dimension you entered is approaching the Maximum Length of 108 in (274 cm). To avoid an Overmax Penalty of US$1,025 or return of your shipment, you will be asked to verify your dimensions and weight by submitting photos documenting the measurements and weight before receiving your shipping label(s).
      "AlertOverDimLimit", // The dimension you entered exceeds the Maximum Length of 108 in (274 cm). If your box or case exceeds the Maximum Length when scanned by our carrier, you will be charged a carrier penalty of US$1,025, your shipment may be stopped or returned, and your package may not be delivered. Please contact Support for packing suggestions to get your shipment’s length down to or below 108 in (274 cm).
      "AlertNearWeightLimit", // The weight you entered is approaching the Maximum Weight of 150 lbs (68 kg). To avoid an Overmax Penalty of US$1,025 or return of your shipment, you will be asked to verify your dimensions and weight by submitting photos documenting the measurements and weight before receiving your shipping label(s).
      "AlertOverWeightLimit", // The weight you entered exceeds the Maximum Weight of 150 lbs (68 kg). If you ship a box or case that exceeds the Maximum Weight when weighed by our carrier, you will be charged a carrier penalty of US$1,025, your shipment may be stopped or returned, and your package may not be delivered. Please contact Support for packing suggestions to get your shipment’s weight down to or below 150 lbs (68 kg).
      "MultiplePackageHighValue", // Some of the packages in your shipment qualify for Preferred Handling. To ensure the whole shipment receives the same service level, we suggest you select Protection levels for each that include Preferred Handling.
      "AlertEstimatedDimensions", // You have selected an estimate box size intended for you to help get an idea of shipping rates. Before booking please verify and adjust your box dimensions by measuring to avoiding additional charges and delivery delays.
      "AlertFreightSize", // Your packaging exceeds our size/weight restrictions. Please contact Support for packing assistance so you can take advantage of our services.
      "AlertPostalCodeLookup", // Unable to lookup your postal code right now, no problem though.
      "CommercialNoValue", // Customs does not permit a value of zero. Please enter the value on your invoice, auction receipt or repair order. We will request a proof of value matching the amount you enter here when processing your shipping documents.
      "PersonalNoValue", // Customs does not permit a value of zero. We will ask you to select your Protection value later in the order process.
      "ProtectionTooHigh", // You can't purchase more protection than the value of your bike.
      "PPLessThanMV", // The amount of Premium Protection you selected is LESS THAN the Market Value you entered. Per our Shipping Policy, you are responsible for the difference in the event that accidental loss or damage occurs during transit. We recommend that you purchase Premium Protection for the Market Value of your shipment.
      "AlertEbikeShipment", // BikeFlights’ e-bike shipping program is for safely shipping single e-bikes that have their battery either contained within the frame or fully installed.
      "AlertEBikeInternational", // E-bike shipping is currently available via ground service within the contiguous U.S.
      "AlertFedExFailed", // We were not able to get rates from the information you provided. Typically the reason is that you gave us too much or too little detail. Try typing something as simple as a basic street address or a city, state, and zip code combination to get rates. Thank you.
      "AlertFedExCanadaNoResults", // Sorry! We were not able to get rates from the information you provided. Typically the reason is that you gave us too much or too little detail. Try typing something as simple as a basic street address or a city and state combination to get rates. Thank you.
      "AlertMaxStageWarning", // At this time you can only search for 6 stages at a time. Contact BikeFlights support to get a bulk quote,
      "SciconWarning", // Due to the frequent occurrence of damage, we do not recommend that you ship your bike in a SCICON Bike Case.
      "AlertHeavyWeightNoVerify", // At over 70 pounds, your package is heavy, and you’ll need to use a sturdy box and packaging to ship safely
      "AlertHeavyWeightVerify", // At over 100 pounds, your package is very heavy, and you’ll need to use a very sturdy box and packaging to ship safely. You will be asked to verify your dimensions and weight by submitting photos documenting the measurements and weight before receiving your shipping label(s).
    ];
    this.alertTypes = {
      country: this.countryAlerts,
      rates: this.rateAlerts,
      weightNoVerify: this.weightNoVerifyAlerts,
      weightVerify: this.weightVerifyAlerts,
      near: this.nearAlerts,
      over: this.overAlerts,
      value: this.valueAlerts,
      protection: this.protectionAlerts,
      content: this.contentAlerts,
      purpose: this.purposeAlerts,
      stages: this.stageAlerts,
    };
  }

  public async Validate(scope: string = "") {
    // for later Validate needs some kind of flag to asks for a particular validation type, depth or scope. so that only that method is called
    this.validated = true;
    // added to prevent validate stages when using on /yourboxes validations
    if (scope === "packages") {
      this.validatePackageSelection(false);
    } else {
      this.validatePackageSelection();
      this.validateStages();
      this.validateAddressDetails();
      this.validateGoogleSelection();
      // if no errors
      if (this.validationErrors?.length === 0) {
        // set boxes, trips and others
        this.validated = false;
        if (this.component) {
          this.component?.setShipMode(this.component.currentShipMode); // move this to component, after validation returns true
          this.component?.setTrips(this.component.trips); // move this to component, after validation returns true
          this.component?.setBoxes(this.component.boxes); // move this to component, after validation returns true
          this.component?.setUnits(this.component.currentUnit); // move this to component, after validation returns true
          this.component?.setPurpose(this.component.purposeSelected); // move this to component, after validation returns true
        }
      } else {
        this.validationMade = true;
      }
    }
    return !this.validationErrors?.length;
  }

  public resetValidations() {
    this.validated = false;
    this.validationMade = false;
    this.validationErrors = [];
    this.component?.clearActiveNotices();
  }

  public requestAlert(data: any) {
    this.component?.requestAlert(this.getFilteredAlertKey(data, "asObject"));
  }

  public addNotice(data: any) {
    this.component?.addNotice(this.getFilteredAlertKey(data));
  }

  public removeNotice(data: any) {
    this.component?.removeNotice(this.getFilteredAlertKey(data));
  }

  protected getFilteredAlertKey(
    data: any,
    returnType: string = "asString"
  ): any {
    const keyGetter = new AlertRequestQuery();

    if (data.hasOwnProperty("type")) {
      switch (data.type) {
        case "country":
          keyGetter.name =
            this.alertTypes[data.type].selector[data.event.direction];
          keyGetter.typeSelector = data.event.direction;
          keyGetter.replacements = {
            [this.alertTypes[data.type].needle[0]]: data.event.country,
          };
          break;
        case "rates":
          const countries = this.component?.currentCountries;
          let isCA = false;
          let name = "";
          for (const property in countries) {
            if (countries[property] == "CA") {
              isCA = true;
            }
          }
          const event = data.event;
          if (event && Object.keys(event).length > 0) {
            if (event.hasOwnProperty("results")) {
              if (event.results === false) {
                // timeout
                name = "timeOut";
              } else {
                name = "noResults";
              }
            } else {
              if (isCA) {
                name = "canada";
              } else {
                name = "default";
              }
            }
          }
          if (name) {
            keyGetter.name = this.alertTypes[data.type].selector[name];
            keyGetter.typeSelector = data.name;
          } else {
            return;
          }
          break;
        case "weightNoVerify":
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
        case "weightVerify":
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
        case "near":
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
        case "over":
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
        case "value":
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
        case "content":
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
        case "protection":
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
        case "purpose":
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
        case "stages":
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
      }
    }
    const filteredData = keyGetter.getKey(
      this.alertNames,
      this.alertTypes[data.type]
    );
    if (returnType === "asString") {
      return filteredData.name;
    } else {
      return filteredData;
    }
  }

  // this methods here should be private after Validate is given some kind of flag to asks for a particular validation type, depth or scope.
  // mostly an issue for the search module since its validation is non-standard
  public validatePackageSelection(validatePurpose = true) {
    // triggers on validate, and activates error highlights
    // clear prev errors
    try {
      this.resetValidations();
      const currentUnit = this.component?.currentUnit;
      const boxes = this.component?.boxes;

      const maxL =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxL
          : this.dimensionLimits.in.maxL;
      const maxW =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxW
          : this.dimensionLimits.in.maxW;
      const maxH =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxH
          : this.dimensionLimits.in.maxH;
      const maxWeight =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxWeight
          : this.dimensionLimits.in.maxWeight;
      const maxDimensional =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxDimensional
          : this.dimensionLimits.in.maxDimensional;
      const nearLimitDelta =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.nearLimitDelta
          : this.dimensionLimits.in.nearLimitDelta;
      let greaterProtection = 0;
      let lesserProtection = 0;

      boxes?.forEach((box: Box, index: number) => {
        this.component?.setBoxProperty({
          name: "hasOverMaxPackage",
          value: false,
          index,
        });
        this.component?.cleanBoxErrors(index);
        // This box Size Values
        const thisLength = Math.ceil(box.length ? box.length : 0);
        const thisWidth = Math.ceil(box.width ? box.width : 0);
        const thisHeight = Math.ceil(box.height ? box.height : 0);
        const thisWeight = Math.ceil(box.weight ? box.weight : 0);
        const thisMarketValue = Math.ceil(
          box.marketValue ? box.marketValue : 0
        );
        const thisContent = box.contents;
        const thisProtection = box.premiumProtection;
        const dimensions = new Array();
        dimensions.push(thisLength);
        dimensions.push(thisWidth);
        dimensions.push(thisHeight);
        dimensions.sort(function (a, b) {
          return b - a;
        });
        const dimensionalWeight =
          dimensions[0] + dimensions[1] * 2 + dimensions[2] * 2;

        // Validate E-bike notice
        if (!thisContent || !thisContent.label) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({
              index,
              error: "categorySelectionInvalid",
            });
          this.validationErrors.push("categorySelectionInvalid");
        } else if (
          thisContent &&
          thisContent.label &&
          thisContent.label.includes("E-Bike")
        ) {
          const countries = this.component?.currentCountries;
          let nonUS = false;
          for (const property in countries) {
            if (countries[property] != "US") {
              nonUS = true;
              break;
            }
          }
          if (!nonUS) {
            const trips = this.component?.trips;
            if (trips)
              for (let step = 0; step < trips.length; step++) {
                const from = trips[step].from;
                if (from && from.address_components) {
                  for (let i = 0; i < from.address_components.length; i++) {
                    const component = from.address_components[i];
                    if (
                      component &&
                      component.types[0] &&
                      component.types[0] === "administrative_area_level_1"
                    ) {
                      const state = component.short_name
                        ? component.short_name
                        : "";
                      if (state === "AK" || state === "HI") nonUS = true;
                    }
                  }
                }

                const to = trips[step].to;
                if (to && to.address_components) {
                  for (let i = 0; i < to.address_components.length; i++) {
                    const component = to.address_components[i];
                    if (
                      component &&
                      component.types[0] &&
                      component.types[0] === "administrative_area_level_1"
                    ) {
                      const state = component.short_name
                        ? component.short_name
                        : "";
                      if (state === "AK" || state === "HI") nonUS = true;
                    }
                  }
                }
              }
          }
          if (nonUS) {
            if (
              this.component &&
              typeof this.component.setBoxError === "function"
            )
              this.component.setBoxError({
                index,
                error: "categorySelectionInvalid",
              });
            this.validationErrors.push("EBikeInternational");
            this.requestAlert({ name: "eBike", type: "content" });
          } else {
            this.requestAlert({ name: "USeBike", type: "content" });
          }
        }

        // Validate Market Value
        if (thisMarketValue < this.minMarketValue) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "marketValueInvalid" });
          this.validationErrors.push("marketValueInvalid");
          // this.requestAlert({name:'zero', type:'value'});
        }

        // Validate Protection
        if (!thisProtection || !thisProtection.coverage || thisProtection.coverage > this.maxProtectionValue) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({
              index,
              error: "premiumProtectionInvalid",
            });
          this.validationErrors.push("premiumProtectionInvalid");
          this.requestAlert({ name: "max", type: "protection" });
        } else if (thisProtection.coverage < thisMarketValue) {
          // if package premium had been selected and is lower than market value
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({
              index,
              error: "premiumProtectionInvalid",
            });
          this.requestAlert({ name: "less", type: "protection" });
        } else {
          greaterProtection =
            greaterProtection < thisProtection.coverage
              ? thisProtection.coverage
              : greaterProtection;
          lesserProtection =
            lesserProtection > 0 && lesserProtection < thisProtection.coverage
              ? lesserProtection
              : thisProtection.coverage;
          if (lesserProtection < 1000 && greaterProtection > 1000) {
            // if market value is way too high
            this.requestAlert({ name: "high", type: "protection" });
          }
        }

        // Validate for empty or 0
        if (!thisLength) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "lengthInvalid" });
          this.validationErrors.push("lengthInvalid");
        }
        if (!thisWidth) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "widthInvalid" });
          this.validationErrors.push("widthInvalid");
        }
        if (!thisHeight) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "heightInvalid" });
          this.validationErrors.push("heightInvalid");
        }
        if (!thisWeight) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "weightInvalid" });
          this.validationErrors.push("weightInvalid");
        }

        // validate for Length
        if (thisLength > maxL) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "lengthInvalid" });
          this.validationErrors.push("lengthInvalid");
          this.requestAlert({ name: "size", type: "over" });
        }
        // Validate Width
        else if (thisWidth > maxW) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "widthInvalid" });
          this.validationErrors.push("widthInvalid");
          this.requestAlert({ name: "size", type: "over" });
        }
        // Validate Height
        else if (thisHeight > maxH) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "heightInvalid" });
          this.validationErrors.push("heightInvalid");
          this.requestAlert({ name: "size", type: "over" });
        }
        // Validate Weight
        else if (thisWeight > maxWeight) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "weightInvalid" });
          this.validationErrors.push("weightInvalid");
          this.requestAlert({ name: "weight", type: "over" });
        }
        // check dimensions
        else if (dimensionalWeight > maxDimensional) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
          this.component.setBoxError({ index, error: "dimensionalInvalid" });
          this.validationErrors.push("dimensionalInvalid");
          this.requestAlert({ name: "dim", type: "over" });
          this.removeNotice({ name: "dim", type: "near" });
        } else if (
          dimensionalWeight > maxDimensional - nearLimitDelta &&
          dimensionalWeight <= maxDimensional
        ) {
          this.requestAlert({ name: "dim", type: "near" });
          // BIK-505 hasOverMaxPackage warning within each package
          this.component?.setBoxProperty({
            name: "hasOverMaxPackage",
            value: true,
            index,
          });
        }
      });

      if (validatePurpose) {
        if (
          this.component?.purposeSelected === null ||
          this.component?.purposeSelected === false
        ) {
          this.validationErrors.push("purposeSelectionInvalid");
          this.component.purposeSelected = false;
        }
      }
    } catch (e) {
      console.log(e);
    }
  }

  public validateBoxes() {
    // triggers everytime a box changes/is updated
    // clear prev errors
    try {
      this.resetValidations();
      const currentUnit = this.component?.currentUnit;
      const boxes = this.component?.boxes;
      const maxL =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxL
          : this.dimensionLimits.in.maxL;
      const maxW =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxW
          : this.dimensionLimits.in.maxW;
      const maxH =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxH
          : this.dimensionLimits.in.maxH;
      const maxWeight =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxWeight
          : this.dimensionLimits.in.maxWeight;
      const weightNoVerify =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.weightNoVerify
          : this.dimensionLimits.in.weightNoVerify;
      const weightVerify =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.weightVerify
          : this.dimensionLimits.in.weightVerify;
      const maxDimensional =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxDimensional
          : this.dimensionLimits.in.maxDimensional;
      const nearLimitDelta =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.nearLimitDelta
          : this.dimensionLimits.in.nearLimitDelta;
      let greaterProtection = 0;
      let lesserProtection = 0;
      boxes?.forEach((box: Box, index: number) => {
        this.component?.setBoxProperty({
          name: "hasOverMaxPackage",
          value: false,
          index,
        });
        this.component?.cleanBoxErrors(index);
        // This box Size Values
        const boxLength = Math.ceil(box.length ? box.length : 0);
        const boxWidth = Math.ceil(box.width ? box.width : 0);
        const boxHeight = Math.ceil(box.height ? box.height : 0);
        const boxWeight = Math.ceil(box.weight ? box.weight : 0);
        const boxMarketValue = Math.ceil(box.marketValue ? box.marketValue : 0);
        const boxContent = box.contents;
        const boxProtection = box.premiumProtection;

        // Validate for E-bike content
        if (
          boxContent &&
          boxContent.label &&
          boxContent.label.includes("E-Bike")
        ) {
          const countries = this.component?.currentCountries;
          let nonUS = false;
          for (const property in countries) {
            if (countries[property] !== "US") {
              nonUS = true;
              break;
            }
          }
          if (!nonUS) {
            const trips = this.component?.trips;
            if (trips)
              for (let step = 0; step < trips.length; step++) {
                const from = trips[step].from;
                if (from && from.address_components) {
                  for (let i = 0; i < from.address_components.length; i++) {
                    const component = from.address_components[i];
                    if (
                      component &&
                      component.types[0] &&
                      component.types[0] === "administrative_area_level_1"
                    ) {
                      const state = component.short_name
                        ? component.short_name
                        : "";
                      if (state === "AK" || state === "HI") nonUS = true;
                    }
                  }
                }

                const to = trips[step].to;
                if (to && to.address_components) {
                  for (let i = 0; i < to.address_components.length; i++) {
                    const component = to.address_components[i];
                    if (
                      component &&
                      component.types[0] &&
                      component.types[0] === "administrative_area_level_1"
                    ) {
                      const state = component.short_name
                        ? component.short_name
                        : "";
                      if (state === "AK" || state === "HI") nonUS = true;
                    }
                  }
                }
              }
          }
          if (nonUS) {
            if (
              this.component &&
              typeof this.component.setBoxError === "function"
            )
              this.component.setBoxError({
                index,
                error: "categorySelectionInvalid",
              });
            this.validationErrors.push("EBikeInternational");
            this.requestAlert({ name: "eBike", type: "content" });
          } else {
            this.requestAlert({ name: "USeBike", type: "content" });
          }
        }

        if (box.preset && box.preset.toLowerCase().includes("scicon")) {
          this.requestAlert({ name: "scicon", type: "content" });
        }

        // Validate Market Value
        if (box.marketValue && boxMarketValue < this.minMarketValue) {
          this.requestAlert({ name: "zero", type: "value" });
        }

        // Validate Protection
        if (boxProtection && boxProtection.coverage > this.maxProtectionValue) {
          this.requestAlert({ name: "max", type: "protection" });
        }
        if (boxProtection && boxProtection.coverage < boxMarketValue) {
          // if package premium had been selected and is lower than market value
          this.requestAlert({ name: "less", type: "protection" });
        }

        if (boxProtection) {
          greaterProtection =
            greaterProtection < boxProtection.coverage
              ? boxProtection.coverage
              : greaterProtection;
          lesserProtection =
            lesserProtection > 0 && lesserProtection < boxProtection.coverage
              ? lesserProtection
              : boxProtection.coverage;
          if (lesserProtection < 1000 && greaterProtection > 1000) {
            // if market value is way too high
            this.requestAlert({ name: "high", type: "protection" });
          }
        }

        // Validate Length
        if (boxLength > maxL) {
          this.requestAlert({ name: "size", type: "over" });
        }
        // Validate Width
        else if (boxWidth > maxW) {
          this.requestAlert({ name: "size", type: "over" });
        }
        // Validate Height
        else if (boxHeight > maxH) {
          this.requestAlert({ name: "size", type: "over" });
        }
        // Validate Weight
        else if (boxWeight > maxWeight) {
          this.requestAlert({ name: "weight", type: "over" });
        } else {
          // check near limits
          const L = box.length ? Math.ceil(box.length) : 0;
          const W = box.width ? Math.ceil(box.width) : 0;
          const H = box.height ? Math.ceil(box.height) : 0;
          const dimensions = new Array();
          dimensions.push(L);
          dimensions.push(W);
          dimensions.push(H);
          dimensions.sort(function (a, b) {
            return b - a;
          });
          const dimensionalWeight =
            dimensions[0] + dimensions[1] * 2 + dimensions[2] * 2;

          // check dimensions
          if (dimensionalWeight > maxDimensional) {
            this.validationErrors.push("dimensionalInvalid");
            this.requestAlert({ name: "dim", type: "over" });
            this.removeNotice({ name: "dim", type: "near" });
            this.removeNotice({ name: "weigth", type: "weightNoVerify" });
            this.removeNotice({ name: "weigth", type: "weightVerify" });
          } else if (
            dimensionalWeight > maxDimensional - nearLimitDelta &&
            dimensionalWeight <= maxDimensional
          ) {
            this.requestAlert({ name: "dim", type: "near" });
            // BIK-505 hasOverMaxPackage warning within each package
            this.component?.setBoxProperty({
              name: "hasOverMaxPackage",
              value: true,
              index,
            });
          }

          // check Near Limits
          if (
            ((L >= maxL - nearLimitDelta &&
              dimensionalWeight < maxDimensional) ||
              (W >= maxW - nearLimitDelta &&
                dimensionalWeight < maxDimensional) ||
              H >= maxH - nearLimitDelta) &&
            L <= maxL &&
            W <= maxW &&
            H <= maxH &&
            dimensionalWeight < maxDimensional
          ) {
            this.requestAlert({ name: "size", type: "near" });
            // BIK-505 hasOverMaxPackage warning within each package
            this.component?.setBoxProperty({
              name: "hasOverMaxPackage",
              value: true,
              index,
            });
          }

          let thisWeight = 0;
          if (typeof box.weight === "number") {
            thisWeight = Math.ceil(box.weight);
          }
          if (thisWeight > weightNoVerify && thisWeight <= weightVerify) {
            this.requestAlert({ name: "weight", type: "weightNoVerify" });
          }
          if (
            thisWeight > weightVerify &&
            thisWeight <= maxWeight - nearLimitDelta
          ) {
            this.requestAlert({ name: "weight", type: "weightVerify" });
            this.removeNotice({ name: "weight", type: "weightNoVerify" });
          }
          if (
            thisWeight > maxWeight - nearLimitDelta &&
            thisWeight <= maxWeight
          ) {
            this.requestAlert({ name: "weight", type: "near" });
            // BIK-505 hasOverMaxPackage warning within each package
            this.component?.setBoxProperty({
              name: "hasOverMaxPackage",
              value: true,
              index,
            });
          }
        }
      });
    } catch (e) {
      console.log(e);
    }
  }

  public validateStages() {
    if (this.component?.trips.length >= this.tripsLimit) {
      this.requestAlert({ name: "maxStage", type: "stages" });
    } else {
      this.removeNotice({ name: "maxStage", type: "stages" });
    }
    const index = this.validationErrors?.indexOf("sameLocation");
    if (index !== -1) {
      this.validationErrors?.splice(index, 1);
    }
    const countries = this.component?.currentCountries;
    const allowedCountries = this.component?.allowedCountries;
    for (let i = 0; i < countries?.length; i++) {
      const country = countries[i];
      const isCountryAllowed = allowedCountries.filter(
        (c: Country) => c.code === country?.short_name && c.active
      );
      if (!isCountryAllowed || !isCountryAllowed.length) {
        this.validationErrors.push("invalidCountry");
      }
    }
    for (let step = 0; step < this.component?.trips.length; step++) {
      if (typeof this.component?.trips[step].isSameLocation === "function") {
        if (this.component?.trips[step].isSameLocation()) {
          this.validationErrors.push("sameLocation");
          break;
        }
      } else if (
        this.component?.trips[step].from &&
        this.component?.trips[step].to &&
        this.component?.trips[step].from.formatted_address ===
          this.component?.trips[step].to.formatted_address
      ) {
        this.validationErrors.push("sameLocation");
        break;
      }
    }
  }

  public validateGoogleSelection() {
    if (this.component?.details) {
      for (let step = 0; step < this.component?.details.length; step++) {
        const to = this.component.details[step].to;
        const from = this.component.details[step].from;
        if (
          !to.passedAVS &&
          to.avsChecked &&
          to.searchType.toLowerCase() === "full"
        ) {
          this.component?.addNotice("warningAVSFailed");
        }
        if (
          !from.passedAVS &&
          from.avsChecked &&
          from.searchType.toLowerCase() === "full"
        ) {
          this.component?.addNotice("warningAVSFailed");
        }
      }
    }
  }

  public validateAddressDetails() {
    if (this.component?.details) {
      if (this.component.details.length === 0) {
        this.validationErrors.push("missingAddressFrom0");
        this.validationErrors.push("missingAddressTo0");
        return;
      }
      for (let step = 0; step < this.component.details.length; step++) {
        if (this.component.details[step]?.from) {
          if (!this.component.details[step].from?.isAddressFilled()) {
            this.validationErrors.push("missingFrom" + step);
          } else if (this.validationErrors.includes("missingFrom" + step)) {
            this.validationErrors = this.validationErrors.filter(
              (error) => error !== "missingFrom" + step
            );
          }
        } else {
          this.validationErrors.push("missingAddressFrom" + step);
        }
        if (this.component.details[step].to) {
          if (!this.component.details[step].to?.isAddressFilled()) {
            this.validationErrors.push("missingTo" + step);
          } else if (this.validationErrors.includes("missingTo" + step)) {
            this.validationErrors = this.validationErrors.filter(
              (error) => error !== "missingTo" + step
            );
          }
        } else {
          this.validationErrors.push("missingAddressTo" + step);
        }
      }
    }
  }
}
